import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, MailCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setState("error"); setMessage("This verification link is missing its token."); return; }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setState("success");
        try { await refresh(); } catch (e) { /* refresh is best-effort; verification already succeeded */ }
      } catch (err) {
        setState("error");
        const d = err?.response?.data?.detail;
        setMessage(typeof d === "string" ? d : "We couldn't verify this link.");
      }
    })();
  }, [token, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bf-glow p-4" data-testid="verify-email-page">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl p-8 text-center shadow-2xl">
        <div className="flex items-center justify-center gap-2.5 mb-6"><img src="/logo.png" alt="OWEverit" className="h-8 w-8 rounded-lg" /><div className="font-display font-extrabold text-lg">OWEverit</div></div>

        {state === "verifying" && (
          <><Loader2 className="h-10 w-10 text-teal-500 animate-spin mx-auto" /><p className="text-sm text-muted-foreground mt-4">Verifying your email…</p></>
        )}
        {state === "success" && (
          <>
            <div className="h-14 w-14 rounded-full bg-emerald-500/15 text-emerald-500 flex items-center justify-center mx-auto"><CheckCircle2 className="h-8 w-8" /></div>
            <h1 className="font-display text-xl font-bold mt-4">Email verified!</h1>
            <p className="text-sm text-muted-foreground mt-1">Your account is confirmed and secure. You're all set.</p>
            <button onClick={() => navigate(user ? "/dashboard" : "/login")} data-testid="verify-continue"
              className="mt-6 w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 transition">
              <MailCheck className="h-4 w-4" /> {user ? "Go to dashboard" : "Sign in"}
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="h-14 w-14 rounded-full bg-rose-500/15 text-rose-500 flex items-center justify-center mx-auto"><XCircle className="h-8 w-8" /></div>
            <h1 className="font-display text-xl font-bold mt-4">Verification failed</h1>
            <p className="text-sm text-muted-foreground mt-1" data-testid="verify-error">{message}</p>
            <p className="text-xs text-muted-foreground mt-3">You can request a fresh link from the banner inside the app.</p>
            <button onClick={() => navigate(user ? "/dashboard" : "/login")} className="mt-6 text-xs text-muted-foreground hover:text-foreground" data-testid="verify-back">← {user ? "Back to app" : "Back to sign in"}</button>
          </>
        )}
      </div>
    </div>
  );
}
